import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Send, RotateCcw, X } from "lucide-react";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold/50 outline-none transition-colors";

export function EmailPanel() {
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [editTpl, setEditTpl] = useState(null);

  const load = async () => {
    try {
      const [s, t] = await Promise.all([api.get("/email/settings"), api.get("/email/templates")]);
      setSettings(s.data);
      setTemplates(t.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await api.put("/email/settings", settings);
      setSettings({ ...r.data, has_api_key: settings.has_api_key });
      toast.success("メール設定を保存しました");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/email/test");
      toast.success(`テストメールを ${r.data.to} に送信しました`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  if (!settings) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gold" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="email-panel">
      {/* 送信設定 */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-gold">送信設定（コーポレートドメイン）</p>
          <span
            data-testid="email-key-status"
            className={`rounded border px-2 py-0.5 text-[10px] ${settings.has_api_key ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20" : "bg-red-500/10 text-red-300 border-red-500/20"}`}
          >
            {settings.has_api_key ? "APIキー設定済み" : "APIキー未設定（送信は無効）"}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-white/50 mb-1.5">送信元アドレス（例: no-reply@prcremit.co.jp）</label>
            <input data-testid="email-sender" className={inputCls} value={settings.sender_email} onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5">送信元の表示名</label>
            <input data-testid="email-sender-name" className={inputCls} value={settings.sender_name} onChange={(e) => setSettings({ ...settings, sender_name: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="block text-xs text-white/50 mb-1.5">通知を受け取る管理者メールアドレス</label>
          <input data-testid="email-notify" className={inputCls} value={settings.notify_email} onChange={(e) => setSettings({ ...settings, notify_email: e.target.value })} />
        </div>
        <label className="flex items-center gap-2.5 text-sm text-white/70 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="email-enabled"
            checked={settings.enabled}
            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
            className="h-4 w-4 accent-[#c8a24b]"
          />
          通知メールを有効にする
        </label>
        <div className="flex items-center gap-3 pt-1">
          <button onClick={saveSettings} disabled={saving} data-testid="email-save" className="inline-flex items-center gap-2 rounded-lg bg-gold px-5 py-2.5 text-sm font-medium text-ink hover:bg-gold-light transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} 保存する
          </button>
          <button onClick={sendTest} disabled={testing || !settings.has_api_key} data-testid="email-test" className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-5 py-2.5 text-sm text-white/70 hover:text-gold hover:border-gold/50 transition-colors disabled:opacity-40">
            {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} テスト送信
          </button>
        </div>
      </div>

      {/* テンプレート */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
        <p className="text-sm text-gold">メールテンプレート</p>
        <p className="text-xs text-white/40">テンプレート名をクリックすると件名・本文をポップアップで編集できます。</p>
        <div className="space-y-2" data-testid="email-templates-list">
          {templates.map((t) => (
            <div
              key={t.key}
              onClick={() => setEditTpl({ ...t })}
              data-testid={`email-template-${t.key}`}
              className="group flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-black/20 p-3.5 cursor-pointer hover:border-gold/40 hover:bg-white/[0.04] transition-all"
            >
              <div className="min-w-0">
                <p className="font-jp font-bold text-white group-hover:text-gold transition-colors">{t.label}</p>
                <p className="text-xs text-white/40 mt-0.5 truncate">件名: {t.subject}</p>
              </div>
              {t.customized && (
                <span className="shrink-0 rounded border border-gold/30 bg-gold/10 px-2 py-0.5 text-[10px] text-gold">カスタム済</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {editTpl && (
        <TemplateModal
          tpl={editTpl}
          onClose={() => setEditTpl(null)}
          onSaved={() => { setEditTpl(null); load(); }}
        />
      )}
    </div>
  );
}

function TemplateModal({ tpl, onClose, onSaved }) {
  const [subject, setSubject] = useState(tpl.subject);
  const [body, setBody] = useState(tpl.body);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/email/templates/${tpl.key}`, { subject, body });
      toast.success("テンプレートを保存しました");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/email/templates/${tpl.key}/reset`);
      setSubject(r.data.subject);
      setBody(r.data.body);
      toast.success("初期テンプレートに戻しました");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4" onClick={onClose} data-testid="email-tpl-modal">
      <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-surface overflow-hidden flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-white/10 shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.2em] text-gold/80 font-mono uppercase">Email Template</p>
            <p className="font-jp font-bold text-lg text-white truncate mt-0.5">{tpl.label}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="email-tpl-close" className="text-white/40 hover:text-white transition-colors shrink-0"><X size={20} /></button>
        </div>
        <div className="px-6 sm:px-8 py-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-xs text-white/50 mb-1.5">件名</label>
            <input data-testid="et-subject" className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5">本文</label>
            <textarea data-testid="et-body" rows={12} className={`${inputCls} resize-y font-mono text-xs leading-relaxed`} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <div className="rounded-lg bg-black/30 border border-white/10 p-3">
            <p className="text-[11px] text-white/40 mb-1.5">使える差し込み項目（クリックで本文末尾に追加）:</p>
            <div className="flex flex-wrap gap-1.5">
              {tpl.placeholders.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setBody((b) => `${b}{${p}}`)}
                  data-testid={`et-ph-${p}`}
                  className="rounded border border-white/15 px-2 py-0.5 font-mono text-[10px] text-white/60 hover:text-gold hover:border-gold/40 transition-colors"
                >
                  {`{${p}}`}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 sm:px-8 py-4 border-t border-white/10 bg-black/20 flex items-center gap-3 shrink-0">
          <button type="button" onClick={save} disabled={busy} data-testid="et-save" className="inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-2.5 text-sm font-bold text-ink hover:bg-gold-light transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={15} />} 保存する
          </button>
          <button type="button" onClick={reset} disabled={busy} data-testid="et-reset" className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2.5 text-sm text-white/70 hover:text-gold hover:border-gold/40 transition-colors disabled:opacity-60">
            <RotateCcw size={14} /> 初期に戻す
          </button>
          <button type="button" onClick={onClose} data-testid="et-cancel" className="rounded-lg border border-white/15 px-5 py-2.5 text-sm text-white/70 hover:text-white transition-colors">
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );
}
